import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    signal,
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import {
    StripeFactoryService,
    StripePaymentElementComponent,
} from 'ngx-stripe';
import {
    StripePaymentInfo,
    StripePaymentService,
} from '../../../services/stripe/stripe-payment.service';
import {
    StripeElementsOptions,
    StripePaymentElementOptions,
    PaymentIntentResult,
    StripeIssuingCardNumberDisplayElementOptions,
} from '@stripe/stripe-js';
import { firstValueFrom, switchMap } from 'rxjs';
import { User } from '../../../services/user/user.model';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertService } from '../../../services/alert/alert.service';
import { Router } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { WaitingService } from '../../layout/waiting/waiting.service';

@Component({
    selector: 'wncm-payment',
    templateUrl: './payment.component.html',
    styleUrl: './payment.component.scss',
})
export class PaymentComponent implements OnInit {
    // MARK: CONSTRUCTOR
    constructor(
        private stripeFactory$: StripeFactoryService,
        private formBuilder: UntypedFormBuilder,
        private stripePayment$: StripePaymentService,
        private alert$: AlertService,
        private router: Router,
        private scroller: ViewportScroller,
        private waiting$: WaitingService
    ) {}

    //#region Variables

    //#region STRIPE Init
    stripe = this.stripeFactory$.create();
    paying = signal(false);

    @ViewChild(StripePaymentElementComponent)
    paymentElement!: StripePaymentElementComponent;

    paymentElementForm = this.formBuilder.group({
        name: ['John doe', [Validators.required]],
        email: ['informatique@wancom.fr', [Validators.required]],
        address: [''],
        zipcode: [''],
        city: [''],
    });

    elementsOptions: StripeElementsOptions = {
        locale: 'fr',
        appearance: {
            theme: 'flat',
        },
        clientSecret: null,
    };

    paymentElementOptions: StripePaymentElementOptions = {
        layout: {
            type: 'tabs',
            defaultCollapsed: false,
            radios: false,
            spacedAccordionItems: false,
        },
    };

    paymentInfo?: StripePaymentInfo;

    //#endregion STRIPE Init

    /**
     * `true` si on est en train de faire une demande de paiement
     */
    public isAskingPayment: boolean = false;

    /**
     * Numéro de la commande en cours
     */
    @Input()
    public orderId: number;

    @Output()
    cancelPayment = new EventEmitter();

    public userInfos: User;

    /** IssuingCard - Unused **/
    public cardId: string = 'ic_1ITi6XKYfU8ZP6raDAXem8ql';
    public nonce: string;
    ek: any;
    cardOptions?: StripeIssuingCardNumberDisplayElementOptions;

    //#endregion Variables

    ngOnInit(): void {
        this.userInfos = JSON.parse(localStorage.getItem('account/who-am-i'));

        //Set paymentElementForm
        this.paymentElementForm.controls.name.setValue(
            this.userInfos.firstName + ' ' + this.userInfos.lastName
        );
        this.paymentElementForm.controls.email.setValue(
            this.userInfos.userName.includes('@') ? this.userInfos.userName : ''
        );

        this.askPayment();
    }

    /**
     * @deprecated Pas utilisé mais peut fonctionner si on le refacto
     */
    private createIssuingCard() {
        this.stripe
            .createEphemeralKeyNonce({ issuingCard: this.cardId })
            .pipe(
                switchMap(({ nonce }) => {
                    this.nonce = nonce;
                    const data = { issuing_card: this.cardId, nonce };
                    return this.stripePayment$.createEphemeralKey(data);
                })
            )
            .subscribe({
                next: ({ ephemeralKeySecret }) => {
                    this.ek = ephemeralKeySecret;
                    this.cardOptions = {
                        issuingCard: this.cardId,
                        nonce: this.nonce,
                        ephemeralKeySecret: this.ek,
                        style: {
                            base: {
                                color: '#000',
                                fontSize: '16px',
                            },
                        },
                    };
                },
            });
    }

    /**
     * Créé une demande de paiement pour stripe
     */
    public askPayment() {
        this.isAskingPayment = true;

        const waitingId: string = this.waiting$.start();
        this.stripePayment$.newPayment(this.orderId).subscribe({
            next: (stripePaymentInfo: StripePaymentInfo) => {
                this.waiting$.end(waitingId);
                this.paymentInfo = stripePaymentInfo;

                this.elementsOptions.clientSecret =
                    this.paymentInfo.clientSecret;

                this.isAskingPayment = false;

                setTimeout(() => this.scroller.scrollToAnchor('payBtn'), 1500);
            },
            error: (err: HttpErrorResponse) => {
                this.waiting$.end(waitingId);
                console.error(err);

                this.alert$.show({
                    message:
                        err.error === 'No Organization'
                            ? "Erreur : Le client n'a pas d'enseigne associée"
                            : 'Erreur lors de la demande de paiement',
                    duration: 1700,
                });
            },
        });
    }

    async pay() {
        if (this.paying()) {
            return;
        }
        this.paying.set(true);

        const waitingId: string = this.waiting$.start();
        this.stripe
            .confirmPayment({
                elements: this.paymentElement.elements,
                confirmParams: {},
                redirect: 'if_required',
            })
            .subscribe({
                next: (result) => {
                    this.waiting$.end(waitingId);
                    
                    this.confirmPayment(result);
                    
                },
                error: (err: HttpErrorResponse) => {
                    this.waiting$.end(waitingId);
                    console.error(err);
                },
            });
    }

    async confirmPayment(payment: PaymentIntentResult) {
        if (payment.error) {
            console.error(payment.error);
            this.alert$.show({
                message: 'Erreur : ' + payment.error.message,
                duration: 1700,
            });
            this.paying.set(false);
            return;
        }

        try {
            await firstValueFrom(
                this.stripePayment$.confirmPayment(payment.paymentIntent.id)
            );

            this.alert$.show({
                message: 'Paiement confirmé !',
                duration: 700,
            });
            this.paying.set(false);
            this.clearCampaignLocalStorage();
            this.router.navigateByUrl('/store/pay-confirm/' + this.orderId);
        } catch (e) {
            alert('ERREUR : ' + e.error);
            this.paying.set(false);
        }
    }

    /**
     * Annule le paiement pour réafficher le panier
     */
    goToCart() {
        this.cancelPayment.emit();
    }

    /**
     * Efface les données de la campagne du localStorage
     */
    private clearCampaignLocalStorage() {
        localStorage.removeItem('campaignEndDate');
        localStorage.removeItem('campaignStartDate');
        localStorage.removeItem('campaignName');
        localStorage.removeItem('selectedFace');
        localStorage.removeItem('WVAllTags');
    }
}
